/**
 * Service IAM Account — APIs server-to-server
 * link-phone, link-email, refresh-user-info, update-avatar, reset-avatar
 * 
 * ⚠️ ATTENTION — SERVER-TO-SERVER UNIQUEMENT
 * Ces APIs utilisent la `secret_key` de votre application IAM.
 * Elles doivent être appelées EXCLUSIVEMENT depuis un backend (Node.js, PHP, etc.).
 * Ne JAMAIS appeler ces méthodes depuis du code frontend/navigateur car la
 * secret_key serait exposée dans les DevTools réseau.
 * 
 * @version 2.7.9
 */

import { fetchWithTimeout, getHeaders, getNativeAuthConfig } from './api';
import { nativeAuthService } from './nativeAuth';

import type {
  LinkPhoneRequest,
  LinkPhoneResponse,
  LinkEmailRequest,
  LinkEmailResponse,
  RefreshUserInfoSingleRequest,
  RefreshUserInfoSingleResponse,
  RefreshUserInfoBulkRequest,
  RefreshUserInfoBulkResponse,
  UpdateAvatarRequest,
  UpdateAvatarResponse,
  ResetAvatarRequest,
  ResetAvatarResponse,
} from '../types/native';

// ============================================
// Helper — auto-fill credentials if available
// ============================================

function getCredentialsOrThrow(params: { app_key?: string; secret_key?: string }) {
  const app_key = params.app_key;
  const secret_key = params.secret_key;

  if (!app_key || !secret_key) {
    throw new Error('app_key et secret_key sont requis en paramètre. Ces APIs sont server-to-server et ne peuvent pas utiliser les encrypted_credentials.');
  }

  return { app_key, secret_key };
}

// ============================================
// Service
// ============================================

export const iamAccountService = {

  /**
   * Lier un numéro de téléphone à un compte utilisateur existant
   * POST {iamApiUrl}/sso/link-phone
   */
  async linkPhone(params: Partial<Pick<LinkPhoneRequest, 'app_key' | 'secret_key'>> & Omit<LinkPhoneRequest, 'app_key' | 'secret_key'>): Promise<LinkPhoneResponse> {
    const config = getNativeAuthConfig();
    const { app_key, secret_key } = getCredentialsOrThrow(params);

    return fetchWithTimeout<LinkPhoneResponse>(
      `${config.iamApiUrl}/sso/link-phone`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          app_key,
          secret_key,
          reference: params.reference,
          ccphone: params.ccphone,
          phone: params.phone,
        }),
      },
      config.timeout || 30000
    );
  },

  /**
   * Lier une adresse email à un compte utilisateur existant
   * POST {iamApiUrl}/sso/link-email
   */
  async linkEmail(params: Partial<Pick<LinkEmailRequest, 'app_key' | 'secret_key'>> & Omit<LinkEmailRequest, 'app_key' | 'secret_key'>): Promise<LinkEmailResponse> {
    const config = getNativeAuthConfig();
    const { app_key, secret_key } = getCredentialsOrThrow(params);

    return fetchWithTimeout<LinkEmailResponse>(
      `${config.iamApiUrl}/sso/link-email`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          app_key,
          secret_key,
          reference: params.reference,
          email: params.email,
        }),
      },
      config.timeout || 30000
    );
  },

  /**
   * Récupérer les user_infos d'un utilisateur (mode single)
   * POST {iamApiUrl}/sso/refresh-user-info
   */
  async refreshUserInfo(params: Partial<Pick<RefreshUserInfoSingleRequest, 'secret_key'>> & Omit<RefreshUserInfoSingleRequest, 'secret_key'>): Promise<RefreshUserInfoSingleResponse> {
    const config = getNativeAuthConfig();
    
    const secret_key = params.secret_key;

    if (!secret_key) {
      throw new Error('secret_key est requis. Chargez les credentials ou passez-la en paramètre.');
    }

    return fetchWithTimeout<RefreshUserInfoSingleResponse>(
      `${config.iamApiUrl}/sso/refresh-user-info`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          secret_key,
          alias_reference: params.alias_reference,
        }),
      },
      config.timeout || 30000
    );
  },

  /**
   * Récupérer les user_infos en bulk (max 100 alias_references)
   * POST {iamApiUrl}/sso/refresh-user-info
   */
  async refreshUserInfoBulk(params: Partial<Pick<RefreshUserInfoBulkRequest, 'secret_key'>> & Omit<RefreshUserInfoBulkRequest, 'secret_key'>): Promise<RefreshUserInfoBulkResponse> {
    const config = getNativeAuthConfig();
    
    const secret_key = params.secret_key;

    if (!secret_key) {
      throw new Error('secret_key est requis. Chargez les credentials ou passez-la en paramètre.');
    }

    if (params.alias_references.length > 100) {
      throw new Error('Maximum 100 alias_references par requête bulk.');
    }

    return fetchWithTimeout<RefreshUserInfoBulkResponse>(
      `${config.iamApiUrl}/sso/refresh-user-info`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          secret_key,
          alias_references: params.alias_references,
        }),
      },
      config.timeout || 30000
    );
  },

  /**
   * Mettre à jour l'avatar d'un utilisateur pour une application spécifique
   * POST {iamApiUrl}/sso/update-avatar
   */
  async updateAvatar(params: Partial<Pick<UpdateAvatarRequest, 'app_key' | 'secret_key'>> & Omit<UpdateAvatarRequest, 'app_key' | 'secret_key'>): Promise<UpdateAvatarResponse> {
    const config = getNativeAuthConfig();
    const { app_key, secret_key } = getCredentialsOrThrow(params);

    return fetchWithTimeout<UpdateAvatarResponse>(
      `${config.iamApiUrl}/sso/update-avatar`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          app_key,
          secret_key,
          alias_reference: params.alias_reference,
          avatar_url: params.avatar_url,
        }),
      },
      config.timeout || 30000
    );
  },

  /**
   * Réinitialiser l'avatar d'un utilisateur (remettre à null pour utiliser l'image globale)
   * POST {iamApiUrl}/sso/reset-avatar
   */
  async resetAvatar(params: Partial<Pick<ResetAvatarRequest, 'app_key' | 'secret_key'>> & Omit<ResetAvatarRequest, 'app_key' | 'secret_key'>): Promise<ResetAvatarResponse> {
    const config = getNativeAuthConfig();
    const { app_key, secret_key } = getCredentialsOrThrow(params);

    return fetchWithTimeout<ResetAvatarResponse>(
      `${config.iamApiUrl}/sso/reset-avatar`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          app_key,
          secret_key,
          alias_reference: params.alias_reference,
        }),
      },
      config.timeout || 30000
    );
  },
};
